import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import PageHeader from '../../components/PageHeader';
import CustomModal from '../../components/CustomModal';

const IFStudentAdmissionDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [student, setStudent] = useState(null);
  const [error, setError] = useState(null);
  const [modalImage, setModalImage] = useState({ show: false, url: '', title: '' });

  const isPDF = (url) => {
    if (!url) return false;
    // Check if the URL contains .pdf (case insensitive)
    return url.toLowerCase().split(/[?#]/)[0].endsWith('.pdf');
  };

  const openImagePopup = (url, title) => {
    setModalImage({ show: true, url, title });
  };

  const LMS = process.env.REACT_APP_API_LMS;
  const token = process.env.REACT_APP_API_TOKEN;

  const fetchStudent = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${LMS}/api/student-admissions/${id}`, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Accept': 'application/json'
        }
      });
      const result = await response.json();
      if (result.status) {
        setStudent(result.data);
      } else {
        setError(result.message || 'Failed to fetch student details');
      }
    } catch (error) {
      console.error('Error fetching student details:', error);
      setError('An error occurred while fetching student details');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchStudent();
    }
  }, [id]);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center vh-100">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error || !student) {
    return (
      <div>
        <div className="row">
          <div className="col-12">
            <PageHeader
              title="Student Detail"
              breadcrumbs={[
                { label: "Dashboard", path: "/teacher-dashboard" },
                { label: "Admission Forms", path: "/teacher-student-admissions" },
                { label: "Error", active: true }
              ]}
            />
          </div>
        </div>
        <div className="alert alert-danger mx-3">
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          {error || 'Student not found.'}
          <div className="mt-3">
            <button className="btn btn-primary btn-sm" onClick={() => fetchStudent()}>
              <i className="bi bi-arrow-clockwise me-1"></i> Try Again
            </button>
            <button className="btn btn-outline-secondary btn-sm ms-2" onClick={() => navigate(-1)}>
              Go Back
            </button>
          </div>
        </div>
      </div>
    );
  }

  const Section = ({ title, icon, children }) => (
    <div className="card shadow-sm border-0 rounded-4 mb-4 overflow-hidden">
      <div className="card-header bg-white border-0 py-3 px-4 d-flex align-items-center gap-2">
        <i className={`${icon} text-primary fs-5`}></i>
        <h5 className="mb-0 fw-bold">{title}</h5>
      </div>
      <div className="mx-3 border-bottom border-2"></div>
      <div className="card-body px-4 pb-2 pt-2">
        <div className="row g-3">
          {children}
        </div>
      </div>
    </div>
  );

  const DetailField = ({ label, value, col = "col-md-4 col-lg-3" }) => {
    const displayValue = (!value || value === 'Invalid Date' || value === 'null') ? 'N/A' : value;

    return (
      <div className={col}>
        <label className="text-primary small fw-bold text-uppercase mb-1" style={{ letterSpacing: '0.05em' }}>
          {label}
        </label>
        <div className="fw-medium text-dark">{displayValue}</div>
      </div>
    );
  };

  return (
    <div>
      <div className="row">
        <div className="col-12">
          <PageHeader
            title="Student Detail Information"
            breadcrumbs={[
              { label: "Dashboard", path: "/teacher-dashboard" },
              { label: "Admission Forms", path: "/teacher-student-admissions" },
              { label: `${student.first_name} ${student.last_name}`, active: true }
            ]}
          />
        </div>
      </div>

      <div className="row mb-3">
        <div className="col-12">
          <button className="btn btn-link text-decoration-none ps-0 text-muted" onClick={() => navigate(-1)}>
            <i className="bi bi-arrow-left me-2"></i> Back to List
          </button>
        </div>
      </div>

      <div className="row">
        <div className="col-12">

          {/* Student Info Card */}
          <div className="card shadow-sm border-0 rounded-4 mb-4 overflow-hidden bg-white">
            <div className="card-body p-4">
              <div className="d-flex flex-column flex-md-row align-items-center gap-3 gap-md-4 text-center text-md-start">
                <div className="flex-shrink-0">
                  <div className="position-relative">
                    {student.photo ? (
                      isPDF(student.photo) ? (
                        <div
                          className="rounded-circle bg-light d-flex flex-column align-items-center justify-content-center border border-4 border-light shadow-sm text-danger"
                          style={{ width: '100px', height: '100px' }}
                        >
                          <i className="bi bi-file-pdf fs-2"></i>
                          <span style={{ fontSize: '0.6rem' }}>PDF</span>
                        </div>
                      ) : (
                        <img
                          src={student.photo}
                          alt={`${student.first_name} ${student.last_name}`}
                          className="rounded-circle shadow-sm object-fit-contain border border-4 border-light"
                          style={{ width: '100px', height: '100px' }}
                        />
                      )
                    ) : (
                      <div
                        className="rounded-circle bg-primary bg-opacity-10 d-flex align-items-center justify-content-center text-primary fw-bold"
                        style={{ width: '100px', height: '100px', fontSize: '2rem' }}
                      >
                        {student.first_name.charAt(0)}{student.last_name.charAt(0)}
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex-grow-1 w-100">
                  <div className="d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
                    <div>
                      <h4 className="mb-1 fw-bold text-dark">
                        {student.salutation} {student.first_name} {student.middle_name} {student.last_name}
                      </h4>
                      <div className="text-secondary d-flex align-items-center justify-content-center justify-content-md-start gap-2">
                        <span>{student.education_level}</span>
                      </div>
                      <span className="text-muted d-flex align-items-center justify-content-center justify-content-md-start gap-2">
                        <span>{student.batch_name}</span>
                      </span>
                    </div>
                    <div>
                      {student.is_submitted === 1 && (
                        <span className="badge bg-success bg-opacity-10 text-success border border-success border-opacity-25 px-4 py-2 rounded-pill">
                          <i className="bi bi-check-circle-fill me-1"></i> Submitted
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Section 1: Personal Info */}
          <Section title="Personal Information" icon="bi bi-person-badge">
            <DetailField label="Salutation" value={student.salutation} />
            <DetailField label="First Name" value={student.first_name} />
            <DetailField label="Middle Name" value={student.middle_name} />
            <DetailField label="Last Name" value={student.last_name} />
            <DetailField label="Gender" value={student.gender} />
            <DetailField label="Date of Birth" value={student.date_of_birth} />
            <DetailField label="Marital Status" value={student.marital_status} />
            <DetailField label="Religion" value={student.religion} />
            <DetailField label="Category" value={student.category} />
            <DetailField label="Disability" value={student.disability} />
            {student.disability === 'Yes' && <DetailField label="Type of Disability" value={student.type_of_disability} />}
          </Section>

          {/* Section 2: Family Info */}
          <Section title="Family Details" icon="bi bi-people-fill">
            <DetailField label="Father's First Name" value={student.father_first_name} />
            <DetailField label="Father's Last Name" value={student.father_last_name} />
            <DetailField label="Mother's First Name" value={student.mother_first_name} />
            <DetailField label="Mother's Last Name" value={student.mother_last_name} />
          </Section>

          {/* Section 3: Contact & Identifiers */}
          <Section title="Contact & Identifiers" icon="bi bi-envelope-fill">
            <DetailField label="Email ID" value={student.email_id} />
            <DetailField label="Mobile Number" value={`${student.country_code} ${student.mobile_no}`} />
            <DetailField label="ID Type" value={student.id_type} />
            {student.id_type === 'Alternate ID' && <DetailField label="Type of Alternate ID" value={student.type_of_alternate_id} />}
            <DetailField label="ID Number" value={student.id_no} />
            <DetailField label="Domicile State" value={student.domicile_state} />
            <DetailField label="Domicile District" value={student.domicile_district} />
          </Section>

          {/* Section 4: Address */}
          <Section title="Address Details" icon="bi bi-geo-alt-fill">
            <div className="col-12">
              <h6 className="fw-bold mb-3 text-primary-emphasis">Permanent Address</h6>
              <div className="row g-3">
                <DetailField label="Address" value={student.permanent_address} col="col-12" />
                <DetailField label="State" value={student.permanent_address_state} />
                <DetailField label="District" value={student.permanent_address_district} />
                <DetailField label="Pin Code" value={student.permanent_address_pin_code} />
                <DetailField label="Constituency" value={student.permanent_address_constituency} />
              </div>
            </div>
            <div className="col-12 mt-4 pt-3 border-top">
              <div className="d-flex align-items-center gap-2 mb-3">
                <h6 className="fw-bold mb-0 text-primary-emphasis">Communication Address</h6>
                {student.communication_same_as_permanent === 'Yes' && (
                  <span className="badge bg-secondary bg-opacity-10 text-secondary small py-1 px-2">Same as Permanent</span>
                )}
              </div>
              <div className="row g-3">
                <DetailField label="State" value={student.communication_address_state} />
                <DetailField label="District" value={student.communication_address_district} />
                <DetailField label="Pin Code" value={student.communication_address_pin_code} />
                <DetailField label="Constituency" value={student.communication_address_constituency} />
              </div>
            </div>
          </Section>

          {/* Section 5: Education & Experience */}
          <Section title="Education & Professional Experience" icon="bi bi-book-half">
            <DetailField label="Education Level" value={student.education_level} />
            <DetailField label="Training Status" value={student.training_status} />
            <DetailField label="Previous Sector" value={student.previous_experience_sector} />
            <DetailField label="Experience (Months)" value={student.months_of_experience} />
            <DetailField label="Employed" value={student.employed} />
            <DetailField label="Employment Status" value={student.employment_status} />
            <DetailField label="Employment Details" value={student.employment_details} />
          </Section>

          {/* Section 6: Documents */}
          <Section title="Uploaded Documents" icon="bi bi-file-earmark-richtext-fill">
            <div className="col-md-4">
              <label className="text-primary small fw-bold text-uppercase mb-2 d-block">Aadhaar Card</label>
              {student.aadhaar_card ? (
                <div className="position-relative group">
                  {isPDF(student.aadhaar_card) ? (
                    <div className="bg-light rounded border d-flex flex-column align-items-center justify-content-center p-4 shadow-sm" style={{ height: '150px' }}>
                      <i className="bi bi-file-pdf text-danger fs-1"></i>
                      <span className="small text-muted mt-2">PDF Document</span>
                    </div>
                  ) : (
                    <img src={student.aadhaar_card} alt="Aadhaar" className="img-fluid rounded border shadow-sm w-100 object-fit-contain" style={{ height: '150px' }} />
                  )}
                  <button
                    onClick={() => openImagePopup(student.aadhaar_card, 'Aadhaar Card')}
                    className="btn btn-sm btn-dark position-absolute top-50 start-50 translate-middle opacity-0 group-hover-100 border-0"
                  >
                    <i className={`bi ${isPDF(student.aadhaar_card) ? 'bi-file-earmark-text' : 'bi-eye'}`}></i> {isPDF(student.aadhaar_card) ? 'View PDF' : 'View Full'}
                  </button>
                </div>
              ) : <div className="text-muted">No document uploaded</div>}
            </div>
            <div className="col-md-4">
              <label className="text-primary small fw-bold text-uppercase mb-2 d-block">Highest Qualification Marksheet</label>
              {student.highest_qualification_marksheet ? (
                <div className="position-relative group">
                  {isPDF(student.highest_qualification_marksheet) ? (
                    <div className="bg-light rounded border d-flex flex-column align-items-center justify-content-center p-4 shadow-sm" style={{ height: '150px' }}>
                      <i className="bi bi-file-pdf text-danger fs-1"></i>
                      <span className="small text-muted mt-2">PDF Document</span>
                    </div>
                  ) : (
                    <img src={student.highest_qualification_marksheet} alt="Marksheet" className="img-fluid rounded border shadow-sm w-100 object-fit-contain" style={{ height: '150px' }} />
                  )}
                  <button
                    onClick={() => openImagePopup(student.highest_qualification_marksheet, 'Highest Qualification Marksheet')}
                    className="btn btn-sm btn-dark position-absolute top-50 start-50 translate-middle opacity-0 group-hover-100 border-0"
                  >
                    <i className={`bi ${isPDF(student.highest_qualification_marksheet) ? 'bi-file-earmark-text' : 'bi-eye'}`}></i> {isPDF(student.highest_qualification_marksheet) ? 'View PDF' : 'View Full'}
                  </button>
                </div>
              ) : <div className="text-muted">No document uploaded</div>}
            </div>
            <div className="col-md-4">
              <label className="text-primary small fw-bold text-uppercase mb-2 d-block">Photo</label>
              {student.photo ? (
                <div className="position-relative group">
                  {isPDF(student.photo) ? (
                    <div className="bg-light rounded border d-flex flex-column align-items-center justify-content-center p-4 shadow-sm" style={{ height: '150px' }}>
                      <i className="bi bi-file-pdf text-danger fs-1"></i>
                      <span className="small text-muted mt-2">PDF Document</span>
                    </div>
                  ) : (
                    <img src={student.photo} alt="Student Photo" className="img-fluid rounded border shadow-sm w-100 object-fit-contain" style={{ height: '150px' }} />
                  )}
                  <button
                    onClick={() => openImagePopup(student.photo, 'Student Photo')}
                    className="btn btn-sm btn-dark position-absolute top-50 start-50 translate-middle opacity-0 group-hover-100 border-0"
                  >
                    <i className={`bi ${isPDF(student.photo) ? 'bi-file-earmark-text' : 'bi-eye'}`}></i> {isPDF(student.photo) ? 'View PDF' : 'View Full'}
                  </button>
                </div>
              ) : <div className="text-muted">No photo uploaded</div>}
            </div>
          </Section>

        </div>
      </div>

      <style jsx>{`
        .group:hover .group-hover-100 {
          opacity: 1 !important;
          transition: opacity 0.3s ease;
        }
        .text-primary-emphasis {
          color: #052c65 !important;
        }
        .card {
          border: 1px solid rgba(0,0,0,0.05);
        }
      `}</style>

      {/* Image Popup Modal */}
      <CustomModal
        show={modalImage.show}
        onClose={() => setModalImage({ ...modalImage, show: false })}
        title={modalImage.title}
        width={isPDF(modalImage.url) ? 1000 : 800}
      >
        <div className="text-center p-2">
          {isPDF(modalImage.url) ? (
            <div style={{ height: '80vh', minHeight: '500px' }}>
              <iframe
                src={`${modalImage.url}#toolbar=0`}
                title={modalImage.title}
                width="100%"
                height="100%"
                className="rounded border"
                style={{ border: 'none' }}
              />
            </div>
          ) : (
            <img
              src={modalImage.url}
              alt={modalImage.title}
              className="img-fluid rounded shadow-sm"
              style={{ maxHeight: '80vh', objectFit: 'contain' }}
            />
          )}
        </div>
      </CustomModal>
    </div>
  );
};

export default IFStudentAdmissionDetail;
